import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  MenuItem,
  Button,
  Tooltip,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import { Search } from 'lucide-react';
import { NameEmailAvatar } from '../../../../../../../ui/components/Common/NameEmailAvatar';
import { WellnessCandidate } from '../../../../../../../utils/types/wellness_types';
import { DataTable, TableColumn } from '../../../../../../../ui';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  onPageChange: (p: number) => void;
}

interface CandidateTabProps {
  jobOpeningId: string;
  candidates: WellnessCandidate[];
  isLoading: boolean;
  onSendInvitation: (candidateIds: string[]) => void;
  onSendReminder: (candidateId: string) => void;
  onViewResults: () => void;
  pagination: PaginationProps;
  search: string;
  onSearchChange: (v: string) => void;
  emailSearch: string;
  onEmailSearchChange: (v: string) => void;
  assessmentStatusFilter: string;
  onAssessmentStatusFilterChange: (v: string) => void;
}

const downloadCsv = (candidates: WellnessCandidate[]) => {
  const header = ['Name', 'Email', 'Status', 'Invited', 'Assessment Status', 'Invitation Count'];
  const rows = candidates.map((c) => [
    c.name,
    c.email,
    c.applicantStatus,
    c.invited ? 'YES' : 'NO',
    c.assessmentStatus,
    c.invitationCount,
  ]);
  const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'wellness_candidates.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const TooltipWrapper: React.FC<{ text: string }> = ({ text }) => {
  return (
    <Tooltip title={text || ''} arrow placement='top'>
      <Typography
        variant='body2'
        sx={{
          maxWidth: 180,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
        }}
      >
        {text || '-'}
      </Typography>
    </Tooltip>
  );
};

const formatStatusLabel = (value: string): string => {
  if (!value) return '';
  return value
    .split(/[-_]/)
    .map((part) =>
      part
        .replace(/Inprogress/gi, 'In Progress')
        .replace(/([a-z])([A-Z])/g, '$1 $2'),
    )
    .join(' ')
    .trim();
};

export const CandidateTab: React.FC<CandidateTabProps> = ({
  candidates,
  isLoading,
  onSendInvitation,
  pagination,
  search,
  onSearchChange,
  emailSearch,
  onEmailSearchChange,
  assessmentStatusFilter,
  onAssessmentStatusFilterChange,
}) => {
  const [statusFilter, setStatusFilter] = useState('Wellness');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelectedIds((prev) =>
      prev.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.candidateId)),
    );

  const allSelected = candidates.length > 0 && selectedIds.size === candidates.length;
  const someSelected = selectedIds.size > 0;
  const { currentPage, totalPages, totalRecords, onPageChange } = pagination;

  const columns: TableColumn<WellnessCandidate>[] = [
    {
      key: 'candidate',
      label: 'CANDIDATE',
      width: '30%',
      align: 'left',
      disableSort: true,
      render: (row) => (
        <NameEmailAvatar
          name={row.name}
          email={row.email}
          avatar={undefined}
          avatarWidth='32px'
          avatarHeight='32px'
          nameFontSize='13px'
          emailFontSize='11px'
        />
      ),
    },
    {
      key: 'status',
      label: 'STATUS',
      width: '20%',
      align: 'left',
      disableSort: true,
      render: (row) => <TooltipWrapper text={formatStatusLabel(row.applicantStatus)} />,
    },
    {
      key: 'invited',
      label: 'INVITED',
      width: '13%',
      align: 'left',
      disableSort: true,
      render: (row) => <TooltipWrapper text={row.invited ? 'YES' : 'NO'} />,
    },
    {
      key: 'invitationCount',
      label: 'INVITATIONS',
      width: '12%',
      align: 'left',
      disableSort: true,
      render: (row) => <TooltipWrapper text={String(row.invitationCount)} />,
    },
    {
      key: 'assessmentStatus',
      label: 'ASSESSMENT STATUS',
      width: '25%',
      align: 'left',
      disableSort: true,
      render: (row) => (
        <TooltipWrapper text={row.assessmentStatus === '-' ? '-' : formatStatusLabel(row.assessmentStatus)} />
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Filter row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        {/* Name Search */}
        <TextField
          size='small'
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder='Search by name...'
          sx={{ minWidth: 160, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />

        {/* Email Search */}
        <TextField
          size='small'
          value={emailSearch}
          onChange={(e) => onEmailSearchChange(e.target.value)}
          placeholder='Search by email...'
          sx={{ minWidth: 160, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Search size={16} />
              </InputAdornment>
            ),
          }}
        />

        {/* Applicant Status */}
        <TextField
          select
          size='small'
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value='Wellness'>Wellness</MenuItem>
          <MenuItem value='All'>All Statuses</MenuItem>
        </TextField>

        {/* Assessment status */}
        <TextField
          select
          size='small'
          value={assessmentStatusFilter}
          onChange={(e) => onAssessmentStatusFilterChange(e.target.value)}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value=''>-- All Assessments --</MenuItem>
          <MenuItem value='Assigned'>Not Invited</MenuItem>
          <MenuItem value='Invite Sent'>Invite Sent</MenuItem>
          <MenuItem value='In Progress'>In Progress</MenuItem>
          <MenuItem value='Completed'>Completed</MenuItem>
          <MenuItem value='Expired'>Expired</MenuItem>
        </TextField>

        <Stack direction='row' spacing={1} sx={{ ml: 'auto' }}>
          <Button
            variant='outlined'
            color='inherit'
            onClick={() => downloadCsv(candidates)}
            sx={{ borderRadius: 2, textTransform: 'none', height: 40 }}
          >
            Export To CSV
          </Button>
          <Button
            variant='outlined'
            color='inherit'
            disabled={!someSelected}
            sx={{ borderRadius: 2, textTransform: 'none', height: 40 }}
          >
            Remove Invite
          </Button>
        </Stack>
      </Box>

      {/* Bulk action bar */}
      {someSelected && (
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: '#a7f3d0',
            backgroundColor: '#ecfdf5',
            px: 2,
            py: 1,
          }}
        >
          <Typography variant='body2' sx={{ fontWeight: 600, color: '#065f46' }}>
            {selectedIds.size} selected
          </Typography>
          <Button
            variant='contained'
            size='small'
            onClick={() => {
              onSendInvitation(Array.from(selectedIds));
              setSelectedIds(new Set());
            }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Send Invitation ({selectedIds.size})
          </Button>
          <Button
            size='small'
            onClick={() => setSelectedIds(new Set())}
            sx={{ ml: 'auto', textTransform: 'none', color: 'text.secondary' }}
          >
            Clear
          </Button>
        </Paper>
      )}

      {/* Table */}
      <DataTable<WellnessCandidate>
        isLoading={isLoading}
        columns={columns}
        data={candidates}
        rowsPerPage={20}
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
        hasCheckBox
        onSelectAll={toggleAll}
        onSelectRow={(_rowIndex, row) => toggleSelect(row.candidateId)}
        isRowSelected={(_rowIndex, row) => selectedIds.has(row.candidateId)}
        isAllSelected={allSelected}
        isIndeterminate={someSelected && !allSelected}
      />
    </Box>
  );
};